import React, { useState } from 'react';
import { TrendingDown, Clock, Truck, TrendingUp, Activity } from 'lucide-react';

const TruckResults: React.FC = () => {
  const [hoveredStat, setHoveredStat] = useState<number | null>(null);

  const stats = [
    {
      label: 'Empty Run Reduction',
      value: '12-16%',
      desc: 'Lower unproductive movement through better planning, dispatch visibility, and route allocation.',
      icon: TrendingDown,
      color: 'bg-green-500',
      graph: [82, 78, 72, 68, 64, 60],
    },
    {
      label: 'Dispatch Time Saved',
      value: '10hrs+',
      desc: 'Weekly dispatch coordination time reduced by centralizing trips, drivers, and vehicle status.',
      icon: Clock,
      color: 'bg-blue-500',
      graph: [12, 15, 18, 20, 23, 26],
    },
    {
      label: 'Active Fleet Utilization',
      value: '94%',
      desc: 'Better route planning and execution help operators keep more trucks moving profitably.',
      icon: Truck,
      color: 'bg-purple-500',
      graph: [75, 79, 83, 87, 91, 94],
    },
    {
      label: 'Billing Speed',
      value: '+31%',
      desc: 'Faster invoicing and reconciliation with cleaner trip records and automated operational inputs.',
      icon: TrendingUp,
      color: 'bg-orange-500',
      graph: [40, 45, 51, 58, 64, 71],
    },
  ];

  return (
    <section className="py-24 bg-navy-950 overflow-hidden relative">
      <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] bg-primary-600 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] bg-accent-orange rounded-full blur-[120px]" />
      </div>

      <div className="container-main relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6 border border-white/10 backdrop-blur-sm">
              <Activity className="w-4 h-4 text-green-400" />
              <span className="text-sm font-medium text-white">Freight Impact</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-white font-heading mb-6 leading-tight">
              More Loads. Better Control. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent-orange">
                Stronger Freight Operations
              </span>
            </h2>

            <p className="text-xl text-gray-400 mb-10 leading-relaxed">
              GaadiMatic helps truck operators move from fragmented dispatch, billing, and compliance workflows
              to one operating layer built for day-to-day freight execution.
            </p>

            <div className="flex gap-4">
              <div className="flex-1 bg-white/5 rounded-2xl p-6 border border-white/10 backdrop-blur-md">
                <div className="text-3xl font-bold text-white mb-1">FTL + PTL</div>
                <div className="text-sm text-gray-400">Managed Together</div>
              </div>
              <div className="flex-1 bg-white/5 rounded-2xl p-6 border border-white/10 backdrop-blur-md">
                <div className="text-3xl font-bold text-white mb-1">24/7</div>
                <div className="text-sm text-gray-400">Dispatch Visibility</div>
              </div>
            </div>
          </div>

          <div className="grid gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                onMouseEnter={() => setHoveredStat(index)}
                onMouseLeave={() => setHoveredStat(null)}
                className={`
                  relative bg-white/5 border border-white/10 rounded-2xl p-6
                  transition-all duration-500 ease-out cursor-default
                  ${hoveredStat === index ? 'bg-white/10 border-white/20 translate-x-2 shadow-2xl' : 'hover:bg-white/10'}
                `}
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">{stat.value}</div>
                      <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
                    </div>
                  </div>

                  <div className="h-10 w-24 flex items-end justify-between gap-1">
                    {stat.graph.map((bar, i) => (
                      <div
                        key={i}
                        className={`w-full rounded-t-sm transition-all duration-500 ${hoveredStat === index ? 'bg-white' : 'bg-white/30'}`}
                        style={{ height: `${(bar / Math.max(...stat.graph)) * 100}%` }}
                      />
                    ))}
                  </div>
                </div>

                <div
                  className={`
                    overflow-hidden transition-all duration-300 ease-out
                    ${hoveredStat === index ? 'max-h-20 opacity-100 mt-4' : 'max-h-0 opacity-0'}
                  `}
                >
                  <p className="text-sm text-gray-300 pl-16 border-l-2 border-white/20">{stat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TruckResults;
